import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigationType, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { nodeUrl, categoryUrl, typeBrowsePath } from '@/lib/taxonomyUrl';
import { trackEvent } from '@/lib/analytics';
import { getListCache, setListCache } from '@/lib/listCache';
import { COUNTRIES_OF_ORIGIN } from '@/lib/countries';
import ListingCard from '@/components/marketplace/ListingCard';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetTrigger, SheetContent } from '@/components/ui/sheet';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '@/components/ui/select';
import { SlidersHorizontal, Search, X } from 'lucide-react';
import { SponsoredAdsRow } from '@/components/marketplace/SponsoredAd';
import SmartSlot from '@/components/marketplace/SmartSlot';
import SEOHead from '@/components/marketplace/SEOHead';
import SeoContentBlock from '@/components/marketplace/SeoContentBlock';
import TypeTaxonomyNav from '@/components/marketplace/TypeTaxonomyNav';
import {
  SERVICE_MODE_OPTIONS, TRAVEL_CAPABILITY_OPTIONS,
  RESPONSE_TIME_OPTIONS, EMERGENCY_SERVICE_OPTIONS,
} from '@/lib/service_options';

// Service-specific filter block — only renders when listingType='service'.
// Fetches active certifications & industries once from the master lists.
const ServiceFilters = ({ params, setParam }) => {
  const [certs, setCerts] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [applications, setApplications] = useState([]);
  useEffect(() => {
    api.get('/service-taxonomy/certifications').then(r => setCerts(r.data)).catch(() => setCerts([]));
    api.get('/service-taxonomy/industries').then(r => setIndustries(r.data)).catch(() => setIndustries([]));
    api.get('/service-taxonomy/applications').then(r => setApplications(r.data)).catch(() => setApplications([]));
  }, []);
  return (
    <>
      <div>
        <label className='block text-xs font-medium text-muted-foreground mb-1'>Emergency service</label>
        <Select value={params.emergency_service || ''} onValueChange={(v) => setParam('emergency_service', v === '__all__' ? '' : v)}>
          <SelectTrigger data-testid='browse-emergency-select'><SelectValue placeholder='Any' /></SelectTrigger>
          <SelectContent>
            <SelectItem value='__all__'>Any</SelectItem>
            {EMERGENCY_SERVICE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className='block text-xs font-medium text-muted-foreground mb-1'>Service mode</label>
        <Select value={params.service_mode || ''} onValueChange={(v) => setParam('service_mode', v === '__all__' ? '' : v)}>
          <SelectTrigger data-testid='browse-service-mode-select'><SelectValue placeholder='Any' /></SelectTrigger>
          <SelectContent>
            <SelectItem value='__all__'>Any</SelectItem>
            {SERVICE_MODE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className='block text-xs font-medium text-muted-foreground mb-1'>Travel capability</label>
        <Select value={params.travel_capability || ''} onValueChange={(v) => setParam('travel_capability', v === '__all__' ? '' : v)}>
          <SelectTrigger data-testid='browse-travel-select'><SelectValue placeholder='Any' /></SelectTrigger>
          <SelectContent>
            <SelectItem value='__all__'>Any</SelectItem>
            {TRAVEL_CAPABILITY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className='block text-xs font-medium text-muted-foreground mb-1'>Response time</label>
        <Select value={params.response_time || ''} onValueChange={(v) => setParam('response_time', v === '__all__' ? '' : v)}>
          <SelectTrigger data-testid='browse-response-select'><SelectValue placeholder='Any' /></SelectTrigger>
          <SelectContent>
            <SelectItem value='__all__'>Any</SelectItem>
            {RESPONSE_TIME_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className='block text-xs font-medium text-muted-foreground mb-1'>OEM authorized</label>
        <Select value={params.oem_authorized || ''} onValueChange={(v) => setParam('oem_authorized', v === '__all__' ? '' : v)}>
          <SelectTrigger data-testid='browse-oem-select'><SelectValue placeholder='Any' /></SelectTrigger>
          <SelectContent>
            <SelectItem value='__all__'>Any</SelectItem>
            <SelectItem value='true'>Yes — OEM authorized</SelectItem>
            <SelectItem value='false'>No</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className='block text-xs font-medium text-muted-foreground mb-1'>Min experience (years)</label>
        <Input data-testid='browse-experience-min' type='number' min='0' value={params.experience_min || ''} onChange={(e) => setParam('experience_min', e.target.value)} />
      </div>
      {certs.length > 0 && (
        <div>
          <label className='block text-xs font-medium text-muted-foreground mb-1'>Certification</label>
          <Select value={params.certification || ''} onValueChange={(v) => setParam('certification', v === '__all__' ? '' : v)}>
            <SelectTrigger data-testid='browse-certification-select'><SelectValue placeholder='Any' /></SelectTrigger>
            <SelectContent className='max-h-72'>
              <SelectItem value='__all__'>Any</SelectItem>
              {certs.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {industries.length > 0 && (
        <div>
          <label className='block text-xs font-medium text-muted-foreground mb-1'>Industry served</label>
          <Select value={params.industry || ''} onValueChange={(v) => setParam('industry', v === '__all__' ? '' : v)}>
            <SelectTrigger data-testid='browse-industry-select'><SelectValue placeholder='Any' /></SelectTrigger>
            <SelectContent className='max-h-72'>
              <SelectItem value='__all__'>Any</SelectItem>
              {industries.map(i => <SelectItem key={i.id} value={i.name}>{i.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {applications.length > 0 && (
        <div>
          <label className='block text-xs font-medium text-muted-foreground mb-1'>Application expertise</label>
          <Select value={params.application || ''} onValueChange={(v) => setParam('application', v === '__all__' ? '' : v)}>
            <SelectTrigger data-testid='browse-application-select'><SelectValue placeholder='Any' /></SelectTrigger>
            <SelectContent className='max-h-72'>
              <SelectItem value='__all__'>Any</SelectItem>
              {applications.map(a => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </>
  );
};

const Filters = ({ listingType, params, setParam, setParams, categories, subcategories, states, cities, clearAll,
                   scoped = false, scopedSubs = [], currentSubId = '', onSelectSub }) => (
  <div className='space-y-5'>
    <div>
      <label className='block text-xs font-medium text-muted-foreground mb-1'>Search</label>
      <div className='relative'>
        <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
        <Input
          data-testid='browse-search-input'
          className='pl-8'
          placeholder='Keyword, brand, model...'
          value={params.q || ''}
          onChange={(e) => setParam('q', e.target.value)}
        />
      </div>
    </div>
    {scoped ? (
      /* Category is fixed by the page URL — the Category selector is hidden and
         navigation happens via subcategory chips. We keep a Subcategory selector
         here (synced to the current scope) that NAVIGATES to the clean canonical
         subcategory URL — never a UUID query. */
      scopedSubs && scopedSubs.length > 0 && (
        <div>
          <label className='block text-xs font-medium text-muted-foreground mb-1'>Subcategory</label>
          <Select value={currentSubId || '__all__'} onValueChange={(v) => onSelectSub && onSelectSub(v === '__all__' ? '' : v)}>
            <SelectTrigger data-testid='browse-subcategory-select'><SelectValue placeholder='All subcategories' /></SelectTrigger>
            <SelectContent className='max-h-72'>
              <SelectItem value='__all__'>All subcategories</SelectItem>
              {scopedSubs.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )
    ) : (
      <>
        <div>
          <label className='block text-xs font-medium text-muted-foreground mb-1'>Category</label>
          <Select value={params.category_id || ''} onValueChange={(v) => setParam('category_id', v === '__all__' ? '' : v)}>
            <SelectTrigger data-testid='browse-category-select'><SelectValue placeholder='All categories' /></SelectTrigger>
            <SelectContent>
              <SelectItem value='__all__'>All categories</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {params.category_id && (
          <div>
            <label className='block text-xs font-medium text-muted-foreground mb-1'>Subcategory</label>
            <Select value={params.subcategory_id || ''} onValueChange={(v) => setParam('subcategory_id', v === '__all__' ? '' : v)}>
              <SelectTrigger data-testid='browse-subcategory-select'><SelectValue placeholder='All subcategories' /></SelectTrigger>
              <SelectContent>
                <SelectItem value='__all__'>All subcategories</SelectItem>
                {subcategories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </>
    )}
    <div>
      <label className='block text-xs font-medium text-muted-foreground mb-1'>State</label>
      <Select value={params.state || ''} onValueChange={(v) => setParams({ state: v === '__all__' ? '' : v, city: '' })}>
        <SelectTrigger data-testid='browse-state-select'><SelectValue placeholder='All states' /></SelectTrigger>
        <SelectContent>
          <SelectItem value='__all__'>All states</SelectItem>
          {states.map(s => <SelectItem key={s.state} value={s.state}>{s.state}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
    {params.state && (
      <div>
        <label className='block text-xs font-medium text-muted-foreground mb-1'>City</label>
        <Select value={params.city || ''} onValueChange={(v) => setParam('city', v === '__all__' ? '' : v)}>
          <SelectTrigger data-testid='browse-city-select'><SelectValue placeholder='All cities' /></SelectTrigger>
          <SelectContent>
            <SelectItem value='__all__'>All cities</SelectItem>
            {cities.map(c => <SelectItem key={c.id} value={c.city}>{c.city}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    )}
    {listingType !== 'wanted' && listingType !== 'service' && (
      <div>
        <label className='block text-xs font-medium text-muted-foreground mb-1'>Condition</label>
        <Select value={params.condition || ''} onValueChange={(v) => setParam('condition', v === '__all__' ? '' : v)}>
          <SelectTrigger data-testid='browse-condition-select'><SelectValue placeholder='Any' /></SelectTrigger>
          <SelectContent>
            <SelectItem value='__all__'>Any</SelectItem>
            {['New', 'Like New', 'Used', 'Refurbished'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    )}
    {listingType !== 'service' && (
      <div>
        <label className='block text-xs font-medium text-muted-foreground mb-1'>Country of origin</label>
        <Select value={params.country_of_origin || ''} onValueChange={(v) => setParam('country_of_origin', v === '__all__' ? '' : v)}>
          <SelectTrigger data-testid='browse-country-of-origin-select'><SelectValue placeholder='Any' /></SelectTrigger>
          <SelectContent className='max-h-72'>
            <SelectItem value='__all__'>Any country</SelectItem>
            {COUNTRIES_OF_ORIGIN.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    )}
    {listingType === 'service' && (
      <ServiceFilters params={params} setParam={setParam} />
    )}
    <div className='grid grid-cols-2 gap-2'>
      <div>
        <label className='block text-xs font-medium text-muted-foreground mb-1'>Min ₹</label>
        <Input data-testid='browse-min-price' type='number' value={params.min_price || ''} onChange={(e) => setParam('min_price', e.target.value)} />
      </div>
      <div>
        <label className='block text-xs font-medium text-muted-foreground mb-1'>Max ₹</label>
        <Input data-testid='browse-max-price' type='number' value={params.max_price || ''} onChange={(e) => setParam('max_price', e.target.value)} />
      </div>
    </div>
    <Button variant='outline' className='w-full' onClick={clearAll} data-testid='browse-clear-filters-button'>
      <X className='h-4 w-4 mr-1.5' /> Clear filters
    </Button>
  </div>
);

// Per-listing-type SEO metadata for the Sell / Rent / Wanted / Services index pages.
const BROWSE_SEO = {
  sell: {
    path: 'sell',
    title: 'Sell Industrial Surplus & Equipment Online | Indus Fact',
    description: 'Browse verified sell listings for industrial machines, tools, spares & raw materials over India. Post your listing free on IndusFact today.',
    keywords: 'sell industrial equipment, used machinery India, industrial surplus, sell spares, B2B classifieds',
  },
  rent: {
    path: 'rent',
    title: 'Rent Industrial Machinery & Equipment Online | Indus Fact',
    description: 'Find and rent industrial machines, tools & equipments from verified suppliers over India. Flexible rental listings — post or browse free on Indus Fact.',
    keywords: 'rent industrial machinery, equipment rental India, machine hire, industrial rental listings',
  },
  wanted: {
    path: 'wanted',
    title: 'Wanted Ads: Buy Requests for Industrial Equipment | Indus Fact',
    description: 'Browse wanted ads or post your own buy request for industrial machinery, tools & equipment. Connect directly with verified sellers on Indus Fact.',
    keywords: 'wanted industrial equipment, buy requests, machinery buyers India, post buy requirement',
  },
  service: {
    path: 'services',
    title: 'Industrial Services: AMC, Fabrication & Calibration India | Indus Fact',
    description: 'Find verified industrial service providers for AMC, fabrication, calibration, installation & consultancy over India. Compare, connect & hire on Indus Fact.',
    keywords: 'industrial services India, AMC, fabrication, calibration, installation, consultancy',
  },
};

const Browse = ({ listingType = 'sell', scopedNode = null, scopedParent = null, scopedLevel = 1, scopedPath = '' }) => {
  const scoped = !!scopedNode;
  // In scoped (category/subcategory) mode the taxonomy node is fixed by the
  // clean canonical URL; listings are constrained by category_id (level 1,
  // includes descendants) or subcategory_id (level 2). Filters/sort still layer
  // on top as query state under the existing noindex+canonical rules.
  const scopedBase = scoped
    ? (scopedLevel === 2 ? { subcategory_id: scopedNode.id } : { category_id: scopedNode.id })
    : {};
  const [searchParams, setSearchParams] = useSearchParams();
  const navType = useNavigationType();
  // Cache key is the full filtered URL for this listing type (+ scoped node). On Back/Forward
  // (POP) we hydrate the previously-loaded feed (items + page + sort) so the
  // ScrollManager can restore the user's exact place — instead of remounting,
  // refetching page 1, and losing everything below the fold.
  const cacheKey = `${listingType}::${scoped ? scopedNode.id : ''}::${searchParams.toString()}`;
  const cached = navType === 'POP' ? getListCache(cacheKey) : null;

  const [listings, setListings] = useState(cached ? cached.listings : []);
  const [total, setTotal] = useState(cached ? cached.total : 0);
  const [loading, setLoading] = useState(cached ? false : true);
  // Cursor-friendly pagination: keep bumping `page` and APPEND items. Keeps
  // the browse feed scalable for the OLX/IndiaMART-size catalog we're
  // building for. `page` resets to 1 whenever a filter or sort changes so
  // the reader always starts on freshest results.
  const [page, setPage] = useState(cached ? cached.page : 1);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 24;
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [scopedSubs, setScopedSubs] = useState([]);
  const [catTree, setCatTree] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  // Signature of the data already loaded, so the fetch effect is idempotent
  // (skips when we already hold this exact result — including after a
  // Back/Forward cache hydration, and StrictMode's dev double-mount).
  const loadedSigRef = useRef(cached ? cacheKey : null);

  const p = Object.fromEntries(searchParams.entries());
  // Sort is URL-driven (single source of truth) so a direct ?sort= link works
  // and every sorted permutation is a query-state view → noindex + canonical to
  // the clean URL, exactly like the other filters.
  const sort = p.sort || 'latest';
  const navigate = useNavigate();

  // Legacy redirect: /{type}?category_id=UUID[&subcategory_id=UUID] is no
  // longer a valid destination — 301-style redirect to the clean canonical
  // taxonomy URL, preserving any genuine filters (location/condition/etc.) as
  // query params on the clean page.
  useEffect(() => {
    if (scoped) return; // scoped pages already use the clean canonical URL
    const catId = p.category_id;
    if (!catId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: cat } = await api.get(`/categories/${catId}`);
        let target = null;
        if (p.subcategory_id) {
          const { data: sub } = await api.get(`/categories/${p.subcategory_id}`);
          target = sub?.url_path || categoryUrl(cat?.listing_type, cat?.path_slug, sub?.path_slug);
        } else {
          target = nodeUrl(cat);
        }
        if (!target || cancelled) return;
        // Carry over remaining (non-taxonomy) filters as query params.
        const rest = new URLSearchParams(searchParams);
        rest.delete('category_id');
        rest.delete('subcategory_id');
        const qs = rest.toString();
        navigate(qs ? `${target}?${qs}` : target, { replace: true });
      } catch { /* unknown id → leave as-is (will be noindex-filtered) */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.category_id, p.subcategory_id]);

  const setParam = useCallback((key, val) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val === '' || val === undefined || val === null) next.delete(key);
      else next.set(key, val);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Batch update — atomic; avoids the stale-closure bug where two sequential
  // setParam() calls would each derive `next` from the SAME stale searchParams
  // and the second call would silently overwrite the first (this is what broke
  // the State filter — selecting a state then clearing city wiped state).
  const setParams = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === '' || v === undefined || v === null) next.delete(k);
        else next.set(k, v);
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const clearAll = () => setSearchParams({});
  const setSort = useCallback((v) => setParam('sort', v === 'latest' ? '' : v), [setParam]);

  useEffect(() => {
    api.get('/categories', { params: { listing_type: listingType, parent_id: 'null' } }).then(r => setCategories(r.data));
    api.get('/locations/states').then(r => setStates(r.data));
  }, [listingType]);

  // Unscoped Type page → full taxonomy tree (Category → Subcategories) for the
  // dynamic navigation block. Scoped page → the relevant subcategory set (own
  // children for a category page, siblings for a subcategory page) that drives
  // both the chips and the in-panel Subcategory selector.
  useEffect(() => {
    if (scoped) { setCatTree([]); return; }
    api.get('/categories/tree', { params: { listing_type: listingType } })
      .then(r => setCatTree(r.data || [])).catch(() => setCatTree([]));
  }, [scoped, listingType]);

  useEffect(() => {
    if (!scoped) { setScopedSubs([]); return; }
    const parentId = scopedLevel === 2 ? (scopedParent && scopedParent.id) : scopedNode.id;
    if (!parentId) { setScopedSubs([]); return; }
    api.get('/categories', { params: { listing_type: listingType, parent_id: parentId } })
      .then(r => setScopedSubs(r.data || [])).catch(() => setScopedSubs([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoped, scopedNode && scopedNode.id, scopedParent && scopedParent.id, scopedLevel, listingType]);

  useEffect(() => {
    if (p.category_id) {
      api.get('/categories', { params: { listing_type: listingType, parent_id: p.category_id } })
        .then(r => setSubcategories(r.data));
    } else setSubcategories([]);
  }, [p.category_id, listingType]);

  useEffect(() => {
    if (p.state) api.get('/locations/cities', { params: { state: p.state } }).then(r => setCities(r.data));
    else setCities([]);
  }, [p.state]);

  // P3: admin-managed section landing content (title/meta/intro/content/FAQ).
  const [sectionPage, setSectionPage] = useState(null);
  useEffect(() => {
    api.get(`/section-pages/${listingType}`).then(r => setSectionPage(r.data)).catch(() => setSectionPage(null));
  }, [listingType]);

  useEffect(() => {
    const sig = cacheKey;
    // Already have exactly this result (fresh mount from cache, or StrictMode
    // remount) — don't refetch and clobber the restored feed/scroll.
    if (loadedSigRef.current === sig) return;
    loadedSigRef.current = sig;
    setLoading(true);
    // Any filter / sort change resets pagination back to page 1.
    setPage(1);
    const baseP = scoped
      ? (() => { const c = { ...p }; delete c.category_id; delete c.subcategory_id; return c; })()
      : p;
    const params = { ...baseP, ...scopedBase, listing_type: listingType, sort, limit: PAGE_SIZE, page: 1 };
    api.get('/listings/public', { params })
      .then(r => {
        setListings(r.data.items);
        setTotal(r.data.total);
        setListCache(cacheKey, { listings: r.data.items, total: r.data.total, page: 1, sort });
        // Analytics: fire `search` only when the user actually typed a
        // query (not on every silent filter change) to avoid inflating
        // search-volume metrics.
        if (p.q && p.q.length >= 2) {
          trackEvent('search', {
            search_term: p.q,
            listing_type: listingType,
            result_count: r.data.total ?? (r.data.items?.length || 0),
            category_id: p.category_id || undefined,
            state: p.state || undefined,
            city: p.city || undefined,
          });
        }
      })
      .catch(() => { setListings([]); setTotal(0); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString(), listingType, scoped ? scopedNode.id : '']);

  // Load more — appends the next page of results. Kept as a plain button
  // (not IntersectionObserver auto-load) so the user always has explicit
  // control and analytics/scroll depth stay meaningful.
  const loadMore = async () => {
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const baseP = scoped
        ? (() => { const c = { ...p }; delete c.category_id; delete c.subcategory_id; return c; })()
        : p;
      const params = { ...baseP, ...scopedBase, listing_type: listingType, sort, limit: PAGE_SIZE, page: nextPage };
      const r = await api.get('/listings/public', { params });
      // Guard against filter changes that raced this request — only append
      // if the server still reports the same total (or higher).
      setListings(prev => {
        const seen = new Set(prev.map(x => x.id));
        const fresh = (r.data.items || []).filter(x => !seen.has(x.id));
        const merged = [...prev, ...fresh];
        setListCache(cacheKey, { listings: merged, total, page: nextPage, sort });
        return merged;
      });
      setPage(nextPage);
    } finally {
      setLoadingMore(false);
    }
  };

  const typeLabel = (
    listingType === 'sell' ? 'Sell Listings'
    : listingType === 'wanted' ? 'Wanted / Buy Requirements'
    : listingType === 'rent' ? 'Rent Listings'
    : listingType === 'service' ? 'Industrial Services'
    : 'Listings'
  );

  const activeCategory = categories.find(c => c.id === p.category_id);
  const activeSub = subcategories.find(c => c.id === p.subcategory_id);

  const typeLabelUpper = (listingType || '').toUpperCase();
  const seo = BROWSE_SEO[listingType] || BROWSE_SEO.sell;
  // Filtered / searched / sorted / paginated variants are duplicates of the
  // clean index/category page — canonicalize to the clean path AND noindex them
  // so we never bloat the index with parameterized permutations.
  const isFilteredView = Array.from(searchParams.keys()).length > 0;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  // Scoped subcategory navigation (chips + in-panel selector) → clean canonical
  // URLs only, never UUID query params.
  const currentSubId = scoped && scopedLevel === 2 ? scopedNode.id : '';
  const onSelectSub = useCallback((id) => {
    if (!id) {
      // "All subcategories" → go up to the parent category (level 2) / stay (level 1)
      if (scopedLevel === 2 && scopedParent && scopedParent.url_path) navigate(scopedParent.url_path);
      return;
    }
    const target = scopedSubs.find(s => s.id === id);
    if (target && target.url_path) navigate(target.url_path);
  }, [scopedSubs, scopedLevel, scopedParent, navigate]);

  // Effective SEO — scoped node CMS/meta wins on category pages; admin section
  // CMS wins on Type pages; static defaults otherwise.
  const scopedIndexable = scoped && scopedNode.active && !scopedNode.hidden;
  const metaTitle = scoped
    ? (scopedNode.meta_title || `${scopedNode.name} — ${typeLabelUpper} Listings | IndusFact`)
    : (sectionPage?.meta_title || seo.title);
  const metaDesc = scoped
    ? (scopedNode.meta_description || `Browse ${typeLabelUpper.toLowerCase()} listings for ${scopedNode.name} from verified sellers on IndusFact — compare, connect and get quotes directly.`)
    : (sectionPage?.meta_description || seo.description);
  // Admin-set canonical override wins on the CLEAN (non-filtered) view; filtered
  // permutations always fold into the computed clean canonical.
  const cleanCanonical = scoped ? `${origin}${scopedNode.url_path}` : `${origin}/${seo.path}`;
  const canonicalOverride = !isFilteredView
    ? (scoped ? scopedNode.canonical_url : sectionPage?.canonical_url)
    : null;
  const seoCanonical = canonicalOverride || cleanCanonical;
  const robotsVal = scoped
    ? ((!scopedIndexable || isFilteredView) ? 'noindex,follow' : undefined)
    : (isFilteredView ? 'noindex,follow' : (sectionPage?.robots_meta || 'index,follow'));
  const schemaEndpoint = scoped
    ? ((scopedIndexable && !isFilteredView) ? `/seo/taxonomy?type=${listingType}&path=${encodeURIComponent(scopedPath)}` : undefined)
    : (isFilteredView ? undefined : `/seo/section/${listingType}`);
  const h1Text = scoped ? scopedNode.name : (sectionPage?.title || typeLabel);
  const subtitleText = scoped
    ? `${typeLabelUpper} listings${scopedParent ? ` in ${scopedParent.name}` : ''}`
    : (activeCategory ? `${activeCategory.name}${activeSub ? ` › ${activeSub.name}` : ''}` : 'Explore active listings');

  return (
    <div className='indus-container py-8'>
      <SEOHead
        title={metaTitle}
        description={metaDesc}
        keywords={scoped ? scopedNode.focus_keyword : seo.keywords}
        canonical={seoCanonical}
        image={scoped ? (scopedNode.og_image_url || undefined) : (sectionPage?.og_image_url || undefined)}
        robots={robotsVal}
        schemaEndpoint={schemaEndpoint}
      />
      {scoped && (
        <nav className='text-sm text-muted-foreground mb-3 flex items-center gap-1.5 flex-wrap' data-testid='category-breadcrumb'>
          <Link to='/' className='hover:text-foreground'>Home</Link>
          <span>/</span>
          <Link to={typeBrowsePath(listingType)} className='hover:text-foreground'>{typeLabelUpper}</Link>
          {scopedParent && (
            <>
              <span>/</span>
              <Link to={scopedParent.url_path} className='hover:text-foreground'>{scopedParent.name}</Link>
            </>
          )}
          <span>/</span>
          <span className='text-foreground'>{scopedNode.name}</span>
        </nav>
      )}
      <div className='flex flex-wrap items-end justify-between gap-3 mb-4'>
        <div>
          <h1 className='text-2xl sm:text-3xl font-semibold tracking-tight'>{h1Text}</h1>
          <p className='text-muted-foreground text-sm'>{subtitleText}</p>
        </div>
        <div className='flex gap-2'>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className='w-56' data-testid='browse-sort-select'><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value='latest' data-testid='sort-latest'>Latest first</SelectItem>
              <SelectItem value='oldest' data-testid='sort-oldest'>Oldest first</SelectItem>
              {listingType !== 'wanted' && (
                <>
                  <SelectItem value='price_asc' data-testid='sort-price-asc'>Price: Low to High</SelectItem>
                  <SelectItem value='price_desc' data-testid='sort-price-desc'>Price: High to Low</SelectItem>
                </>
              )}
              <SelectItem value='most_viewed' data-testid='sort-most-viewed'>Most viewed</SelectItem>
              <SelectItem value='most_inquired' data-testid='sort-most-inquired'>Most inquired</SelectItem>
            </SelectContent>
          </Select>
          <Sheet>
            <SheetTrigger asChild className='lg:hidden'>
              <Button variant='outline' data-testid='browse-open-filters-button'><SlidersHorizontal className='h-4 w-4 mr-1.5' /> Filters</Button>
            </SheetTrigger>
            <SheetContent side='left' className='w-80'>
              <div className='mt-4'>
                <Filters listingType={listingType} params={p} setParam={setParam} setParams={setParams} categories={categories} subcategories={subcategories} states={states} cities={cities} clearAll={clearAll} scoped={scoped} scopedSubs={scopedSubs} currentSubId={currentSubId} onSelectSub={onSelectSub} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Sponsored ad row (browse top) */}
      <div className='mb-4' data-testid='browse-sponsored-ads'>
        <SponsoredAdsRow placement='browse_top' listingType={listingType} limit={1} />
      </div>

      {/* Navigation — scoped: subcategory chips; Type page: full Category → Subcategories tree */}
      {scoped ? (
        <div className='mb-6 space-y-2' data-testid='browse-scoped-nav'>
          {(scopedSubs.length > 0 || scopedLevel === 2) && (
            <div className='flex flex-wrap gap-2'>
              {scopedLevel === 2 && scopedParent && (
                <Link
                  to={scopedParent.url_path}
                  className='rounded-full border px-3 py-1 text-xs hover:bg-secondary inline-flex items-center gap-1'
                  data-testid='browse-chip-all-parent'
                >
                  <X className='h-3 w-3' /> All {scopedParent.name}
                </Link>
              )}
              {scopedSubs.map(s => {
                const active = scopedLevel === 2 && s.id === scopedNode.id;
                return (
                  <Link
                    key={s.id}
                    to={nodeUrl(s)}
                    className={`rounded-full px-3 py-1 text-xs transition-colors border ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-secondary'}`}
                    data-testid={`browse-chip-sub-${s.slug}`}
                  >
                    {s.name}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <TypeTaxonomyNav tree={catTree} />
      )}

      <div className='flex gap-6'>
        <aside className='hidden lg:block w-72 shrink-0'>
          <Card className='p-5 sticky top-20'>
            <div className='font-semibold mb-4 flex items-center gap-2'>
              <SlidersHorizontal className='h-4 w-4' /> Filters
            </div>
            <Filters listingType={listingType} params={p} setParam={setParam} setParams={setParams} categories={categories} subcategories={subcategories} states={states} cities={cities} clearAll={clearAll} scoped={scoped} scopedSubs={scopedSubs} currentSubId={currentSubId} onSelectSub={onSelectSub} />
          </Card>
        </aside>

        <div className='flex-1 min-w-0'>
          {loading ? (
            <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'>
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className='p-4 h-72 animate-pulse'></Card>
              ))}
            </div>
          ) : listings.length === 0 ? (
            <Card className='p-10 text-center' data-testid='browse-empty-state'>
              <div className='text-lg font-semibold'>{isFilteredView ? 'No listings match your filters' : (scoped ? 'No listings yet in this category' : 'No listings yet')}</div>
              <div className='text-sm text-muted-foreground mt-1'>{isFilteredView ? 'Try clearing some filters or explore other regions.' : 'Check back soon — new listings are added regularly.'}</div>
              {isFilteredView && (
                <Button variant='outline' onClick={clearAll} className='mt-4' data-testid='browse-empty-clear'>
                  Clear filters
                </Button>
              )}
            </Card>
          ) : (
            <>
              <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4' data-testid='browse-results-grid'>
                {listings.flatMap((l, i) => {
                  const cards = [<ListingCard key={l.id} listing={l} />];
                  // Insert one SmartSlot after every 8 organic cards
                  if ((i + 1) % 8 === 0) {
                    cards.push(
                      <SmartSlot
                        key={`slot-${i}`}
                        placement='listing_grid'
                        listingType={listingType}
                        excludeIds={listings.map(x => x.id)}
                      />
                    );
                  }
                  return cards;
                })}
              </div>
              {/* Load-more control. Kept visible until we've loaded every
                  server-reported result. Count intentionally hidden per
                  product decision — users don't need to see totals. */}
              {listings.length < total && (
                <div className='mt-8 flex flex-col items-center gap-2' data-testid='browse-load-more-wrap'>
                  <Button
                    onClick={loadMore}
                    disabled={loadingMore}
                    variant='outline'
                    className='min-w-[220px]'
                    data-testid='browse-load-more-btn'
                  >
                    {loadingMore ? 'Loading…' : 'Load more listings'}
                  </Button>
                </div>
              )}
              {listings.length > 0 && listings.length >= total && (
                <div className='mt-8 text-center text-xs text-muted-foreground' data-testid='browse-end-of-list'>
                  You&apos;ve reached the end. Try widening filters to see more.
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Admin-managed SEO content + visible FAQ (only on the clean landing) */}
      {!isFilteredView && scoped && scopedNode && (
        <SeoContentBlock
          intro={scopedNode.intro_html}
          content={scopedNode.content_html}
          faqs={scopedNode.faqs}
          testidPrefix='category'
        />
      )}
      {!isFilteredView && !scoped && sectionPage && (
        <SeoContentBlock
          intro={sectionPage.intro_html}
          content={sectionPage.content_html}
          faqs={sectionPage.faqs}
          testidPrefix='section'
        />
      )}
    </div>
  );
};

export default Browse;
